/**
 * RhArretesPanel — l'onglet « Arrêtés » de la brique RH.
 *
 * ─── DEUX VUES, PAS TROIS (retour mairie du 28/07) ──────────────────────────
 *
 * Il y avait une page intermédiaire : « Nouvel arrêté » ouvrait un écran qui ne
 * portait qu'un second bouton « Nouvel arrêté », la liste des actes déjà
 * affichée à côté, et un pavé d'explications. Trois clics pour un formulaire,
 * et deux endroits pour déposer l'original signé.
 *
 * Désormais :
 *
 *   · « Arrêtés »  — le registre partagé (liste, fiche, frise, aperçu, dépôt de
 *                    l'original signé). C'est LÀ que vit le suivi.
 *   · « Rédaction » — le formulaire, et rien d'autre. « Nouvel arrêté » y mène
 *                     directement.
 *   · « Modèles »  — le catalogue.
 *
 * Le dépôt du scan signé et l'impression du projet ne sont plus dupliqués ici :
 * ils appartiennent à la fiche de l'acte, dans le registre.
 */

import { ChampsModele, appliquerChamps, type ChampModele } from '@/components/ChampsModele';
import { ActesBriquePanel } from '@/components/ActesBriquePanel';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { Card, Button, Alert, Field } from '@/components/ui';
import {
  Stamp, Plus, Loader2, AlertCircle, FileCheck2, Layers,
} from 'lucide-react';

const CHAMP = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink focus:border-rh focus:outline-none focus:ring-2 focus:ring-rh';
const ZONE = 'w-full rounded-r-lg border border-border bg-surface/60 p-2.5 text-sm text-ink focus:border-rh focus:outline-none focus:ring-2 focus:ring-rh';

interface Preset {
  id: string; libelle: string; categorie: string | null;
  corps: string; visa: string | null; actif: boolean; ordre: number;
}
export function RhArretesPanel() {
  const [vue, setVue] = useState<'arretes' | 'redaction' | 'modeles'>('arretes');
  const [arType, setArType] = useState('');
  const [briquesST, setBriquesST] = useState<string[]>([]);
  // Le circuit d'un modèle : les relecteurs à ajouter AU circuit commun.
  const [circuitModele, setCircuitModele] = useState<string | null>(null);
  const [modeleOuvert, setModeleOuvert] = useState<
    { id: string; libelle: string; code: string; brique: string | null;
      categorie: string | null; corps?: string;
      metadonnees?: Record<string, string> } | null>(null);
  const [circuit, setCircuit] = useState<
    { id: string; role: string; nom: string; email: string | null; commun: boolean }[]>([]);
  const [relecteursDispo, setRelecteursDispo] = useState<
    { id: string; nom: string; email: string }[]>([]);
  const [champs, setChamps] = useState<ChampModele[]>([]);
  const [valeursChamps, setValeursChamps] = useState<Record<string, string>>({});
  const [moQ, setMoQ] = useState('');
  const [moBrique, setMoBrique] = useState('');
  const [moCat, setMoCat] = useState('');
  const [presets, setPresets] = useState<Preset[]>([]);
  // Le CATALOGUE COMMUN (mig 257) : les 150 modèles, toutes briques. La RH émet
  // aussi des actes du service technique — on ne la limite donc pas à `rh`.
  const [modeles, setModeles] = useState<{
    id: string; libelle: string; code: string;
    brique: string | null; categorie: string | null;
    corps?: string; metadonnees?: Record<string, string>;
  }[]>([]);
  const [modeleId, setModeleId] = useState('');
  const [agents, setAgents] = useState<Array<{ id: string; nom: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const [presetId, setPresetId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [objet, setObjet] = useState('');
  const [corps, setCorps] = useState('');
  const [saving, setSaving] = useState(false);
  // Le dernier objet posé PAR un modèle. Sert à distinguer « l'objet vient du
  // canevas » (on peut le remplacer) de « le rédacteur l'a écrit » (on n'y
  // touche pas).
  const objetDuModeleRef = useRef('');

  const [formPreset, setFormPreset] = useState(false);
  const [pLibelle, setPLibelle] = useState('');
  const [pCategorie, setPCategorie] = useState('');
  const [pCorps, setPCorps] = useState('');

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Plus de `api.rh.arretes.list()` ici : la liste des actes est celle du
      // registre, rendue par `ActesBriquePanel`. La charger deux fois donnait
      // deux listes à tenir à jour — et elles divergeaient.
      const p = await api.rh.arretes.presets(true);
      setPresets(p.presets);
      // Silencieux : une commune qui n'a pas encore importé le catalogue doit
      // pouvoir continuer à travailler sans modèle.
      try { setModeles(await api.acteModeles.list()); } catch { setModeles([]); }
      try { setBriquesST(await api.acteModeles.briquesServiceTechnique()); }
      catch { setBriquesST([]); }
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  useEffect(() => {
    void (async () => {
      try {
        const d = await api.rh.dossiersAgents();
        setAgents(d.map((a) => ({ id: a.id, nom: `${a.prenom} ${a.nom}` })));
      } catch { /* la liste n'est utile qu'à la rédaction */ }
    })();
  }, []);

  /**
   * Choisir un modèle remplit l'objet et le corps, variables déjà résolues
   * (commune, maire, tribunal…). Le rédacteur n'a plus qu'à compléter les trous
   * — c'est tout l'intérêt d'avoir 150 canevas.
   */
  async function choisirModele(id: string, pourAgent = agentId) {
    setModeleId(id);
    if (!id) return;
    try {
      const r = await api.acteModeles.preparer(id,
        pourAgent ? { sourceType: 'agent', sourceId: pourAgent } : undefined);
      setCorps(r.corps);
      setChamps(r.champs);
      setValeursChamps({});
      // ─── L'OBJET SUIT LE MODÈLE (retour mairie du 28/07) ─────────────────
      //
      // Le garde `if (!objet.trim())` ne servait qu'une fois : dès le premier
      // modèle choisi, l'objet était figé. On passait à « Prorogation de
      // stage » ou à « Interdiction de divagation des animaux » et l'écran
      // affichait toujours « Avancement d'échelon » — et c'est cet objet-là qui
      // partait sur l'acte.
      //
      // Désormais l'objet suit le modèle, SAUF si le rédacteur l'a réécrit
      // lui-même : sa formulation est alors plus précise que le libellé du
      // canevas, et l'écraser serait pire que le figer.
      setObjet((actuel) =>
        !actuel.trim() || actuel === objetDuModeleRef.current ? r.modele.libelle : actuel);
      objetDuModeleRef.current = r.modele.libelle;
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Modèle illisible');
    }
  }

  /**
   * Changer d'AGENT re-résout le modèle.
   *
   * Les variables de l'agent — grade, cadre d'emplois, échelon, indice majoré,
   * durée hebdomadaire — sont résolues au moment où le modèle est préparé. Si
   * l'agent était choisi APRÈS le modèle, elles restaient des trous à saisir à
   * la main alors que le dossier les portait déjà.
   */
  function choisirAgent(id: string) {
    setAgentId(id);
    if (modeleId) void choisirModele(modeleId, id);
  }

  /**
   * Les modèles proposés à la rédaction : restreints au type choisi, comme le
   * fait le service technique avec son menu Type. Sur 150 modèles, choisir sans
   * filtre est impraticable.
   */
  const modelesProposes = arType ? modeles.filter((m) => m.categorie === arType) : modeles;

  /** Les modèles retenus par les trois filtres de l'onglet Modèles. */
  const modelesFiltres = modeles.filter((m) => {
    if (moBrique && m.brique !== moBrique) return false;
    if (moCat && m.categorie !== moCat) return false;
    const q = moQ.trim().toLowerCase();
    if (!q) return true;
    return m.libelle.toLowerCase().includes(q)
      || (m.categorie ?? '').toLowerCase().includes(q)
      || (m.code ?? '').toLowerCase().includes(q);
  });

  /** Ouvrir ou fermer un domaine de modèles au service technique. */
  async function basculerBriqueST(b: string) {
    const suivant = briquesST.includes(b)
      ? briquesST.filter((x) => x !== b)
      : [...briquesST, b];
    setSaving(true); setError(null);
    try { setBriquesST(await api.acteModeles.setBriquesServiceTechnique(suivant)); }
    catch (e) { setError(e instanceof ApiException ? e.payload.error : 'Réglage impossible'); }
    finally { setSaving(false); }
  }

  /** Ouvre le circuit d'un modèle : commun + compléments propres. */
  async function ouvrirRelecteurs(modeleId: string) {
    setCircuitModele(modeleId); setError(null);
    try {
      const [c, r] = await Promise.all([
        api.acteModeles.circuit(modeleId),
        api.serviceTechnique.arretes.relecteurs.list(),
      ]);
      setCircuit(c);
      setRelecteursDispo(r.items.map((x) => ({ id: x.id, nom: x.nom, email: x.email })));
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Circuit illisible');
    }
  }

  async function creerArrete() {
    if (!agentId || !objet.trim()) return;
    setSaving(true); setError(null);
    try {
      await api.rh.arretes.creer({
        presetId: presetId || null,
        agentId,
        objet: objet.trim(),
        // Les champs saisis sont substitués ; ceux laissés vides gardent leurs
        // accolades, pour que le trou reste visible sur le document.
        corps: corps.trim() ? appliquerChamps(corps, valeursChamps) : undefined,
      });
      setPresetId(''); setModeleId(''); setAgentId(''); setObjet(''); setCorps('');
      setChamps([]); setValeursChamps({});
      // On retourne au registre : le brouillon qu'on vient de créer y est, avec
      // sa frise et son dépôt de scan. Rester sur un formulaire vide laissait
      // croire que rien ne s'était passé.
      setVue('arretes');
      setMsg('Brouillon créé — il est dans le registre ci-dessous.');
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Création impossible');
    } finally { setSaving(false); }
  }

  async function creerPreset() {
    if (pLibelle.trim().length < 2) return;
    setSaving(true); setError(null);
    try {
      await api.rh.arretes.creerPreset({
        libelle: pLibelle.trim(),
        categorie: pCategorie.trim() || null,
        corps: pCorps,
      });
      setFormPreset(false); setPLibelle(''); setPCategorie(''); setPCorps('');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.payload.error : 'Création impossible');
    } finally { setSaving(false); }
  }

  if (loading) {
    return (
      <Card variant="glass" padded>
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
      {msg && <Alert variant="success" icon={FileCheck2}>{msg}</Alert>}

      <div className="flex flex-wrap items-center gap-1.5">
        <Button variant={vue === 'arretes' ? 'primary' : 'ghost'} size="sm"
          onClick={() => setVue('arretes')}>
          <Stamp className="h-4 w-4" aria-hidden="true" /> Arrêtés
        </Button>

        <Button variant={vue === 'modeles' ? 'primary' : 'ghost'} size="sm"
          onClick={() => setVue('modeles')}>
          <Layers className="h-4 w-4" aria-hidden="true" /> Modèles
        </Button>

        {/* Le bouton d'action, à droite : il ouvre la rédaction sans occuper un
            onglet. L'ancien onglet rejouait une liste et des actions que la
            fiche du Suivi porte déjà. */}
        {vue === 'arretes' && (
          <Button variant="primary" size="sm" className="ml-auto"
            onClick={() => setVue('redaction')}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Nouvel arrêté
          </Button>
        )}
      </div>

      {/* L'onglet Arrêtés EST le panneau partagé : liste filtrable, fiche avec
          la frise du circuit, l'aperçu du document et le dépôt de l'original
          signé. Sans `brique` : la RH suit TOUS les actes de la commune. */}
      {vue === 'arretes' && <ActesBriquePanel encastre accent="rh" />}

      {/* ══ Rédaction ══ */}
      {vue === 'redaction' && (
        <>
          <div className="flex justify-start">
            <Button variant="glass" size="sm" onClick={() => setVue('arretes')}>
              ← Retour aux arrêtés
            </Button>
          </div>

          {/* Le formulaire, directement. Il n'y a plus d'écran d'attente entre
              « Nouvel arrêté » et la saisie. */}
          <Card variant="glass" padded>
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Type d'arrêté" htmlFor="ar-type"
                  hint="Restreint la liste des modèles proposés">
                  <select id="ar-type" value={arType}
                    onChange={(e) => { setArType(e.target.value); setModeleId(''); }}
                    className={CHAMP}>
                    <option value="">Tous les types</option>
                    {[...new Set(modeles.map((m) => m.categorie).filter(Boolean) as string[])]
                      .sort().map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Modèle" htmlFor="ar-preset">
                  <select id="ar-preset" value={modeleId}
                    onChange={(e) => void choisirModele(e.target.value)} className={CHAMP}>
                    <option value="">— Sans modèle —</option>
                    {/* Groupés par domaine : la RH retrouve les siens en tête
                        mais garde accès à ceux du service technique. */}
                    {[...new Set(modelesProposes.map((m) => m.brique ?? 'Autres'))].sort().map((g) => (
                      <optgroup key={g} label={g}>
                        {modelesProposes.filter((m) => (m.brique ?? 'Autres') === g).map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.categorie ? `${m.categorie} — ` : ''}{m.libelle}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </Field>
                <Field label="Agent concerné *" htmlFor="ar-agent">
                  <select id="ar-agent" value={agentId}
                    onChange={(e) => setAgentId(e.target.value)} className={CHAMP}>
                    <option value="">— Choisir —</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.nom}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Objet *" htmlFor="ar-objet">
                <input id="ar-objet" value={objet} onChange={(e) => setObjet(e.target.value)}
                  maxLength={300} className={CHAMP} />
              </Field>
              <ChampsModele
                champs={champs}
                valeurs={valeursChamps}
                onChange={(cle, v) => setValeursChamps((p) => ({ ...p, [cle]: v }))}
              />

              <Field label="Corps (vide = repris du modèle)" htmlFor="ar-corps">
                <textarea id="ar-corps" value={corps} onChange={(e) => setCorps(e.target.value)}
                  rows={5} className={ZONE} />
              </Field>
              <div className="flex gap-2">
                <Button variant="primary" size="sm" loading={saving}
                  disabled={!agentId || !objet.trim()} onClick={() => void creerArrete()}>
                  Créer le brouillon
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setVue('arretes')}>
                  Annuler
                </Button>
              </div>
            </div>
          </Card>

        </>
      )}

      {/* ══ Modèles ══ */}
      {vue === 'modeles' && (
        <>
          {!formPreset && (
            <div className="flex justify-end">
              <Button variant="primary" size="sm" onClick={() => setFormPreset(true)}>
                <Plus className="h-4 w-4" aria-hidden="true" /> Nouveau modèle
              </Button>
            </div>
          )}

          {formPreset && (
            <Card variant="glass" padded>
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Nom du modèle *" htmlFor="pr-lib">
                    <input id="pr-lib" value={pLibelle} onChange={(e) => setPLibelle(e.target.value)}
                      maxLength={150} placeholder="Ex : Arrêté d'avancement d'échelon"
                      className={CHAMP} />
                  </Field>
                  <Field label="Catégorie" htmlFor="pr-cat">
                    <input id="pr-cat" value={pCategorie} onChange={(e) => setPCategorie(e.target.value)}
                      maxLength={80} placeholder="Carrière, maladie, discipline…"
                      className={CHAMP} />
                  </Field>
                </div>
                <Field label="Corps du modèle" htmlFor="pr-corps">
                  <textarea id="pr-corps" value={pCorps} onChange={(e) => setPCorps(e.target.value)}
                    rows={8} className={ZONE}
                    placeholder="Variables disponibles : {{agent_nom}}, {{date_du_jour}}" />
                </Field>
                <div className="flex gap-2">
                  <Button variant="primary" size="sm" loading={saving}
                    disabled={pLibelle.trim().length < 2} onClick={() => void creerPreset()}>
                    Créer le modèle
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setFormPreset(false)}>
                    Annuler
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {modeleOuvert && (
            <Card variant="glass" padded>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <span className="font-mono text-2xs text-ink-subtle">{modeleOuvert.code}</span>
                  <h3 className="font-medium text-ink">{modeleOuvert.libelle}</h3>
                  <p className="text-2xs text-ink-subtle">
                    {[modeleOuvert.brique, modeleOuvert.categorie].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <Button variant="ghost" size="sm"
                  onClick={() => { setModeleOuvert(null); setCircuitModele(null); }}>
                  Fermer
                </Button>
              </div>

              {/* Le point de vigilance d'abord : c'est lui qui évite un acte
                  annulable. */}
              {modeleOuvert.metadonnees?.point_de_vigilance && (
                <Alert variant="error" className="mt-3">
                  {modeleOuvert.metadonnees.point_de_vigilance}
                </Alert>
              )}

              {modeleOuvert.metadonnees && (
                <dl className="mt-3 grid gap-2 sm:grid-cols-2">
                  {([['controle_legalite', 'Contrôle de légalité'],
                    ['publicite', 'Publicité'],
                    ['destinataires_notification', 'Notification'],
                    ['delai_recours', 'Recours'],
                    ['base_juridique', 'Base juridique'],
                    ['conservation_archives', 'Conservation']] as [string, string][])
                    .filter(([k]) => modeleOuvert.metadonnees?.[k])
                    .map(([k, label]) => (
                      <div key={k}>
                        <dt className="text-2xs uppercase tracking-wide text-ink-subtle">{label}</dt>
                        <dd className="text-sm text-ink-muted">{modeleOuvert.metadonnees?.[k]}</dd>
                      </div>
                    ))}
                </dl>
              )}

              {modeleOuvert.corps && (
                <div className="mt-3">
                  <h4 className="text-2xs uppercase tracking-wide text-ink-subtle">Texte du modèle</h4>
                  <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded-r-lg border border-border bg-surface/40 p-3 font-sans text-sm text-ink-muted">
                    {modeleOuvert.corps}
                  </pre>
                </div>
              )}
            </Card>
          )}

          {circuitModele && (
            <Card variant="glass" padded>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-ink">Relecteurs de ce modèle</h3>
                  <p className="mt-1 text-sm text-ink-muted">
                    Ils s'ajoutent au circuit commun de la commune. Sans complément,
                    seul le circuit commun s'applique.
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setCircuitModele(null)}>
                  Fermer
                </Button>
              </div>

              <ul className="mt-3 divide-y divide-border">
                {circuit.map((i) => (
                  <li key={i.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                    <span className="text-ink">
                      {i.nom}
                      <span className="ml-2 text-2xs text-ink-subtle">
                        {i.commun ? 'circuit commun' : 'propre au modèle'} · {i.role}
                      </span>
                    </span>
                    {!i.commun && (
                      <Button variant="ghost" size="sm" disabled={saving}
                        onClick={async () => {
                          setSaving(true);
                          try {
                            await api.acteModeles.retirerDuCircuit(i.id);
                            await ouvrirRelecteurs(circuitModele);
                          } finally { setSaving(false); }
                        }}>
                        Retirer
                      </Button>
                    )}
                  </li>
                ))}
                {circuit.length === 0 && (
                  <li className="py-2 text-sm text-ink-subtle">
                    Aucun relecteur — ni commun, ni propre à ce modèle.
                  </li>
                )}
              </ul>

              <div className="mt-3 border-t border-border pt-3">
                <Field label="Ajouter un relecteur à ce modèle" htmlFor="cm-add"
                  hint="Piochés dans les adresses de relecture de la commune (Administration → Relecteurs)">
                  <select id="cm-add" value="" disabled={saving} className={CHAMP}
                    onChange={async (e) => {
                      if (!e.target.value || !circuitModele) return;
                      setSaving(true); setError(null);
                      try {
                        await api.acteModeles.ajouterAuCircuit(circuitModele, {
                          role: 'relecteur', relecteurId: e.target.value,
                        });
                        await ouvrirRelecteurs(circuitModele);
                      } catch (err) {
                        setError(err instanceof ApiException ? err.payload.error : 'Ajout impossible');
                      } finally { setSaving(false); }
                    }}>
                    <option value="">— Choisir —</option>
                    {relecteursDispo
                      .filter((r) => !circuit.some((c) => c.nom === r.nom))
                      .map((r) => <option key={r.id} value={r.id}>{r.nom} · {r.email}</option>)}
                  </select>
                </Field>
              </div>
            </Card>
          )}

          {/* ── Ce que la RH met à disposition du service technique ────────
              Demande d'Henrico : « c'est le module RH qui décidera ce qu'on
              peut donner comme arrêté à un manager de brique ». Le réglage est
              ici, avec le catalogue, pas dans un écran de paramètres à part. */}
          <Card variant="glass" padded>
            <h3 className="font-medium text-ink">Modèles ouverts au service technique</h3>
            <p className="mt-1 text-sm text-ink-muted">
              Le service technique ne voit que les domaines cochés. La RH, elle,
              voit tout le catalogue.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {[...new Set(modeles.map((m) => m.brique).filter(Boolean) as string[])]
                .sort().map((b) => {
                  const ouverte = briquesST.includes(b);
                  return (
                    <button
                      key={b} type="button" disabled={saving}
                      onClick={() => void basculerBriqueST(b)}
                      aria-pressed={ouverte}
                      className={`h-8 rounded-r-pill border px-3 text-xs transition-colors ${
                        ouverte
                          ? 'border-rh/50 bg-rh/10 font-medium text-ink'
                          : 'border-border bg-surface/50 text-ink-muted'
                      }`}
                    >
                      {ouverte ? '✓ ' : ''}{b}
                    </button>
                  );
                })}
            </div>
          </Card>

          {/* ── Le CATALOGUE de la commune : les 150 modèles livrés ────────
              Filtrable par brique et par catégorie, exactement comme ils sont
              rangés. C'est ici que la RH voit tout et attribue aux briques. */}
          <Card variant="glass" padded>
            <div className="mb-3 flex flex-wrap items-end gap-3">
              <div className="min-w-[200px] flex-1">
                <Field label="Rechercher" htmlFor="mo-q">
                  <input id="mo-q" value={moQ} onChange={(e) => setMoQ(e.target.value)}
                    placeholder="Nomination, circulation, débit de boissons…" className={CHAMP} />
                </Field>
              </div>
              <div className="min-w-[180px]">
                <Field label="Brique" htmlFor="mo-b">
                  <select id="mo-b" value={moBrique}
                    onChange={(e) => { setMoBrique(e.target.value); setMoCat(''); }}
                    className={CHAMP}>
                    <option value="">Toutes</option>
                    {[...new Set(modeles.map((m) => m.brique).filter(Boolean) as string[])]
                      .sort().map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
              </div>
              <div className="min-w-[180px]">
                <Field label="Type" htmlFor="mo-c">
                  <select id="mo-c" value={moCat} onChange={(e) => setMoCat(e.target.value)}
                    className={CHAMP}>
                    <option value="">Tous</option>
                    {[...new Set(modeles
                      .filter((m) => !moBrique || m.brique === moBrique)
                      .map((m) => m.categorie).filter(Boolean) as string[])]
                      .sort().map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
              <span className="pb-3 text-sm text-ink-subtle">
                {modelesFiltres.length} / {modeles.length}
              </span>
            </div>

            {modeles.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-muted">
                Catalogue vide. Il est normalement livré avec l'application —
                signalez-le si cet écran reste vide.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {modelesFiltres.map((m) => (
                  <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <div className="min-w-0">
                      <p className="text-sm text-ink">{m.libelle}</p>
                      <p className="text-2xs text-ink-muted">
                        {[m.brique, m.categorie].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      <Button variant="ghost" size="sm"
                        onClick={() => { setModeleOuvert(m); void ouvrirRelecteurs(m.id); }}>
                        Ouvrir
                      </Button>
                      <Button variant="ghost" size="sm"
                        onClick={() => { setModeleId(m.id); void choisirModele(m.id); setVue('redaction'); }}>
                        Utiliser ce modèle
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card variant="glass" padded>
            {presets.length === 0 ? (
              <div className="py-4 text-center">
                <p className="text-sm text-ink-muted">
                  Vos modèles propres apparaîtront ici, à côté du catalogue livré.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {presets.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2 py-2">
                    <div className="min-w-0">
                      <p className={`text-sm text-ink ${p.actif ? '' : 'line-through opacity-60'}`}>
                        {p.libelle}
                      </p>
                      {p.categorie && (
                        <p className="text-2xs text-ink-muted">{p.categorie}</p>
                      )}
                    </div>
                    <Button variant="ghost" size="sm"
                      onClick={async () => {
                        await api.rh.arretes.majPreset(p.id, { actif: !p.actif });
                        await charger();
                      }}>
                      {p.actif ? 'Désactiver' : 'Réactiver'}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
